import React, { useState, useEffect } from "react";
import VerificationCodeInput from "../forms/VerificationCodeInput";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import SmallAlert from "../alerts/SmallAlert";

/**
 * TOTPSetupModal - Works for both Login Flow and Settings Page
 * @param {Object} props
 * @param {string} [props.mfaSetupToken] - Optional setup token for unauthenticated login flow
 * @param {Function} props.onSetupComplete - Callback on activation success
 */
const TOTPSetupModal = ({ mfaSetupToken, onSetupComplete }) => {
  const [qrCode, setQrCode] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Helper to build headers & fetch options based on auth type
  const getFetchOptions = (method, body = null) => {
    const headers = { "Content-Type": "application/json" };
    if (mfaSetupToken) {
      headers["Authorization"] = `Bearer ${mfaSetupToken}`;
    }
    return {
      method,
      headers,
      credentials: mfaSetupToken ? "omit" : "include",
      ...(body ? { body: JSON.stringify(body) } : {}),
    };
  };

  useEffect(() => {
    const fetchSetupData = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
        const response = await fetch(
          `${apiBaseUrl}/auth/totp/setup`,
          getFetchOptions("GET"),
        );

        const data = await response.json();
        if (response.ok) {
          setQrCode(data.qr_code);
          setSecretKey(data.secret_key);
        } else {
          setError(data.message || "Failed to initialize MFA setup.");
        }
      } catch (err) {
        setError("Network error loading QR code.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSetupData();
  }, [mfaSetupToken]);

  const handleActivate = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
      const response = await fetch(
        `${apiBaseUrl}/auth/totp/activate`,
        getFetchOptions("POST", { verification_code: code }),
      );

      const data = await response.json();

      if (response.ok) {
        if (onSetupComplete) onSetupComplete(code);
      } else {
        setError(data.message || "Invalid verification code.");
      }
    } catch (err) {
      setError("Network error activating 2FA.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: "20px 0", width: "100%" }}>
        <LoadingPlaceholder rows={2} type="loading-card" />
      </div>
    );
  }

  return (
    <div
      className="totp-setup-wrapper"
      style={{ width: "100%", textAlign: "center" }}
    >
      {error && (
        <SmallAlert
          alert="True"
          type="error"
          message={error}
          style={{ marginBottom: 15 }}
        />
      )}

      {qrCode && (
        <div style={{ margin: "15px 0" }}>
          <img
            src={qrCode}
            alt="TOTP QR Code"
            style={{
              width: "180px",
              height: "180px",
              margin: "0 auto",
              display: "block",
            }}
          />
        </div>
      )}

      {secretKey && (
        <p
          style={{ fontSize: "12px", color: "#64748b", wordBreak: "break-all" }}
        >
          Manual Entry Key: <strong>{secretKey}</strong>
        </p>
      )}

      <p
        className="verification-message"
        style={{ fontSize: "13px", marginTop: "15px" }}
      >
        Scan the QR code with your authenticator app, then enter the 6-digit
        code:
      </p>

      <VerificationCodeInput
        value={code}
        onChange={setCode}
        disabled={isSubmitting}
      />

      <div
        className="verification-actions margin-top"
        style={{ marginTop: "20px" }}
      >
        <button
          type="button"
          className="cta-button primary"
          onClick={handleActivate}
          disabled={code.length !== 6 || isSubmitting}
        >
          {isSubmitting ? "Enabling..." : "Enable Authenticator"}
        </button>
      </div>
    </div>
  );
};

export default TOTPSetupModal;
